import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Download, Trash2, Eye, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

type FileType = 'csv' | 'xlsx';

// Tabelas suportadas e seus campos editáveis
const SUPPORTED_TABLES = {
  collects: {
    label: 'Coletas Recorrentes (collects)',
    idField: 'collect_id',
    editableFields: [
      { name: 'weight', label: 'Peso (kg)', type: 'number' },
      { name: 'units', label: 'Unidades', type: 'number' },
      { name: 'volume', label: 'Volume (m³)', type: 'number' },
      { name: 'sla_status', label: 'Status SLA', type: 'string' },
      { name: 'kind_of_material', label: 'Tipo de Material', type: 'string' },
      { name: 'type_of_packaging', label: 'Tipo de Embalagem', type: 'string' },
      { name: 'mtr_status', label: 'Status MTR', type: 'string' },
      { name: 'mtr_link', label: 'Link MTR', type: 'string' },
      { name: 'impediment_reason', label: 'Motivo Impedimento', type: 'string' },
      { name: 'hauler_name', label: 'Nome Transportador', type: 'string' },
    ],
  },
  on_demand_collects: {
    label: 'Coletas Sob Demanda (on_demand_collects)',
    idField: 'id',
    editableFields: [
      { name: 'units', label: 'Unidades', type: 'number' },
      { name: 'status_collect', label: 'Status', type: 'string' },
      { name: 'kind_of_material', label: 'Tipo de Material', type: 'string' },
      { name: 'type_of_packaging', label: 'Tipo de Embalagem', type: 'string' },
      { name: 'observations', label: 'Observações', type: 'string' },
      { name: 'scheduled_date', label: 'Data Agendada', type: 'string' },
      { name: 'scheduled_shift', label: 'Turno Agendado', type: 'string' },
    ],
  },
  establishments: {
    label: 'Estabelecimentos (establishments)',
    idField: 'establishment_id',
    editableFields: [
      { name: 'establishment_name', label: 'Nome', type: 'string' },
      { name: 'cnpj', label: 'CNPJ', type: 'string' },
      { name: 'address_street', label: 'Rua', type: 'string' },
      { name: 'address_number', label: 'Número', type: 'string' },
      { name: 'address_complement', label: 'Complemento', type: 'string' },
      { name: 'address_neighborhood', label: 'Bairro', type: 'string' },
      { name: 'address_city', label: 'Cidade', type: 'string' },
      { name: 'address_state', label: 'Estado', type: 'string' },
      { name: 'zip_code', label: 'CEP', type: 'string' },
      { name: 'deleted_at', label: 'Data de Exclusão (deleted_at)', type: 'nullable_date' },
    ],
  },
  generators: {
    label: 'Geradores (generators)',
    idField: 'generator_id',
    editableFields: [
      { name: 'generator_name', label: 'Nome', type: 'string' },
    ],
  },
};

// Type-safe helper functions to avoid deep type instantiation issues
type GenericRecord = Record<string, unknown>;

async function fetchRecord(tableName: string, idField: string, recordId: string): Promise<{ data: GenericRecord | null; error: { message: string } | null }> {
  const client = supabase as unknown as { from: (table: string) => { select: (cols: string) => { eq: (col: string, val: string) => { single: () => Promise<{ data: GenericRecord | null; error: { message: string } | null }> } } } };
  return client.from(tableName).select('*').eq(idField, recordId).single();
}

async function updateRecord(tableName: string, idField: string, recordId: string, updateData: GenericRecord): Promise<{ data: GenericRecord | null; error: { message: string } | null }> {
  const client = supabase as unknown as { from: (table: string) => { update: (data: GenericRecord) => { eq: (col: string, val: string) => { select: (cols: string) => { single: () => Promise<{ data: GenericRecord | null; error: { message: string } | null }> } } } } };
  return client.from(tableName).update(updateData).eq(idField, recordId).select('*').single();
}

type TableName = keyof typeof SUPPORTED_TABLES;

interface CSVRow {
  [key: string]: string;
}

interface UpdateResult {
  rowIndex: number;
  id: string;
  status: 'success' | 'error' | 'skipped';
  message: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

const BulkUpdate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [selectedTable, setSelectedTable] = useState<TableName | ''>('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [limit, setLimit] = useState<string>('');
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [results, setResults] = useState<UpdateResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, successCount: 0, errorCount: 0 });
  const [fileType, setFileType] = useState<FileType>('csv');

  // Get current table config
  const tableConfig = selectedTable ? SUPPORTED_TABLES[selectedTable] : null;
  const idField = tableConfig?.idField || 'id';

  // Handle table selection
  const handleTableChange = (value: string) => {
    setSelectedTable(value as TableName);
    setSelectedFields([]);
    setCsvData([]);
    setCsvHeaders([]);
    setFileName('');
    setValidationErrors([]);
    setResults([]);
    setShowResults(false);
  };

  // Handle field selection
  const toggleField = (fieldName: string) => {
    setSelectedFields(prev => 
      prev.includes(fieldName)
        ? prev.filter(f => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  // Detect CSV delimiter (comma, semicolon, or tab)
  const detectDelimiter = (text: string): string => {
    const firstLine = text.split('\n')[0];
    const semicolonCount = (firstLine.match(/;/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    const tabCount = (firstLine.match(/\t/g) || []).length;
    
    if (semicolonCount > commaCount && semicolonCount > tabCount) return ';';
    if (tabCount > commaCount && tabCount > semicolonCount) return '\t';
    return ',';
  };

  // Parse a single CSV line respecting quoted values
  const parseCSVLine = (line: string, delimiter: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim().replace(/\r$/, ''));
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim().replace(/\r$/, ''));
    return result;
  };

  // Parse CSV
  const parseCSV = (text: string): { headers: string[]; rows: CSVRow[] } => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('O CSV deve ter pelo menos 2 linhas (cabeçalho + dados)');
    }

    const delimiter = detectDelimiter(text);
    console.log('CSV delimiter detected:', delimiter === '\t' ? 'TAB' : delimiter);
    console.log('Total lines in file:', lines.length);

    const headers = parseCSVLine(lines[0], delimiter);
    console.log('Headers found:', headers);
    
    const rows: CSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines
      
      const values = parseCSVLine(line, delimiter);
      if (values.length !== headers.length) {
        console.warn(`Line ${i + 1} skipped: expected ${headers.length} columns, got ${values.length}`);
        continue;
      }
      
      const row: CSVRow = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });
      rows.push(row);
    }

    console.log('Total rows parsed:', rows.length);
    return { headers, rows };
  };

  // Validate CSV
  const validateCSV = useCallback((headers: string[], rows: CSVRow[]): string[] => {
    const errors: string[] = [];

    if (!tableConfig) {
      errors.push('Selecione uma tabela antes de importar o CSV');
      return errors;
    }

    // Check if ID field is present
    if (!headers.includes(idField)) {
      errors.push(`O CSV deve conter a coluna "${idField}" para identificar os registros`);
    }

    // Check if all headers match valid columns
    const validColumns = [idField, ...tableConfig.editableFields.map(f => f.name)];
    const invalidHeaders = headers.filter(h => !validColumns.includes(h));
    if (invalidHeaders.length > 0) {
      errors.push(`Colunas inválidas no CSV: ${invalidHeaders.join(', ')}`);
    }

    // Check if at least one editable field is present
    const editableInCSV = headers.filter(h => tableConfig.editableFields.some(f => f.name === h));
    if (editableInCSV.length === 0) {
      errors.push('O CSV deve conter pelo menos um campo editável');
    }

    // Check selected fields match CSV headers
    if (selectedFields.length > 0) {
      const missingFields = selectedFields.filter(f => !headers.includes(f));
      if (missingFields.length > 0) {
        errors.push(`Campos selecionados não encontrados no CSV: ${missingFields.join(', ')}`);
      }
    }

    // Check for empty IDs
    const emptyIdRows = rows.filter((row, idx) => !row[idField] || row[idField].trim() === '');
    if (emptyIdRows.length > 0) {
      errors.push(`${emptyIdRows.length} linha(s) com ${idField} vazio`);
    }

    return errors;
  }, [tableConfig, idField, selectedFields]);

  // Parse XLSX file
  const parseXLSX = (data: ArrayBuffer): { headers: string[]; rows: CSVRow[] } => {
    const workbook = XLSX.read(data, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Convert to JSON with header row as array of arrays
    const jsonData = XLSX.utils.sheet_to_json<unknown[]>(worksheet, { header: 1 });
    
    if (jsonData.length < 2) {
      throw new Error('O arquivo deve ter pelo menos 2 linhas (cabeçalho + dados)');
    }

    const headerRow = jsonData[0] as unknown[];
    const headers = headerRow.map(h => String(h || '').trim());
    console.log('XLSX Headers found:', headers);
    
    const rows: CSVRow[] = [];
    for (let i = 1; i < jsonData.length; i++) {
      const rowData = jsonData[i] as unknown[];
      if (!rowData || rowData.length === 0) continue;
      
      const row: CSVRow = {};
      headers.forEach((header, index) => {
        const value = rowData[index];
        row[header] = value !== undefined && value !== null ? String(value).trim() : '';
      });
      rows.push(row);
    }

    console.log('Total rows parsed from XLSX:', rows.length);
    return { headers, rows };
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isCSV = file.name.endsWith('.csv');
    const isXLSX = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (fileType === 'csv' && !isCSV) {
      toast({
        title: 'Arquivo inválido',
        description: 'Por favor, selecione um arquivo CSV',
        variant: 'destructive',
      });
      return;
    }

    if (fileType === 'xlsx' && !isXLSX) {
      toast({
        title: 'Arquivo inválido',
        description: 'Por favor, selecione um arquivo XLSX ou XLS',
        variant: 'destructive',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let headers: string[];
        let rows: CSVRow[];

        if (fileType === 'csv') {
          const text = e.target?.result as string;
          const parsed = parseCSV(text);
          headers = parsed.headers;
          rows = parsed.rows;
        } else {
          const data = e.target?.result as ArrayBuffer;
          const parsed = parseXLSX(data);
          headers = parsed.headers;
          rows = parsed.rows;
        }
        
        const errors = validateCSV(headers, rows);
        setValidationErrors(errors);
        setCsvHeaders(headers);
        setCsvData(rows);
        setFileName(file.name);

        if (errors.length === 0) {
          // Auto-select fields from file
          const editableInFile = headers.filter(h => 
            tableConfig?.editableFields.some(f => f.name === h)
          );
          setSelectedFields(editableInFile);
          toast({
            title: 'Arquivo importado com sucesso',
            description: `${rows.length} linhas encontradas`,
          });
        }
      } catch (error) {
        toast({
          title: 'Erro ao processar arquivo',
          description: error instanceof Error ? error.message : 'Erro desconhecido',
          variant: 'destructive',
        });
      }
    };

    if (fileType === 'csv') {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  // Clear file
  const clearFile = () => {
    setCsvData([]);
    setCsvHeaders([]);
    setFileName('');
    setValidationErrors([]);
    setResults([]);
    setShowResults(false);
  };

  // Get rows to process (respecting limit)
  const rowsToProcess = useMemo(() => {
    if (!limit || limit === '') return csvData;
    const limitNum = parseInt(limit, 10);
    if (isNaN(limitNum) || limitNum <= 0) return csvData;
    return csvData.slice(0, limitNum);
  }, [csvData, limit]);

  // Process updates
  const processUpdates = async () => {
    if (!tableConfig || !selectedTable) return;

    setIsProcessing(true);
    setShowConfirmDialog(false);
    const updateResults: UpdateResult[] = [];
    
    // Initialize progress
    const total = rowsToProcess.length;
    setProgress({ current: 0, total, successCount: 0, errorCount: 0 });

    const fieldsToUpdate = selectedFields.length > 0 
      ? selectedFields 
      : csvHeaders.filter(h => tableConfig.editableFields.some(f => f.name === h));

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < rowsToProcess.length; i++) {
      const row = rowsToProcess[i];
      const recordId = row[idField];

      if (!recordId || recordId.trim() === '') {
        updateResults.push({
          rowIndex: i + 2, // +2 porque linha 1 é cabeçalho e array começa em 0
          id: '(vazio)',
          status: 'skipped',
          message: 'ID vazio',
          before: null,
          after: null,
        });
        setProgress({ current: i + 1, total, successCount, errorCount });
        continue;
      }

      try {
        // Fetch current record (before) using helper function
        const { data: beforeData, error: fetchError } = await fetchRecord(selectedTable, idField, recordId);

        if (fetchError || !beforeData) {
          errorCount++;
          updateResults.push({
            rowIndex: i + 2,
            id: recordId,
            status: 'error',
            message: `Registro não encontrado: ${fetchError?.message || 'ID inexistente'}`,
            before: null,
            after: null,
          });
          setProgress({ current: i + 1, total, successCount, errorCount });
          continue;
        }

        // Build update object
        const updateData: Record<string, unknown> = {};
        let hasConversionError = false;
        
        for (const field of fieldsToUpdate) {
          const fieldConfig = tableConfig.editableFields.find(f => f.name === field);
          if (!fieldConfig) continue;

          let value: unknown = row[field];
          
          // Type conversion
          if (fieldConfig.type === 'number') {
            if (value === '' || value === null || value === undefined) {
              value = null;
            } else {
              const numValue = parseFloat(value as string);
              if (isNaN(numValue)) {
                errorCount++;
                updateResults.push({
                  rowIndex: i + 2,
                  id: recordId,
                  status: 'error',
                  message: `Valor inválido para campo numérico "${field}": ${row[field]}`,
                  before: beforeData,
                  after: null,
                });
                hasConversionError = true;
                break;
              }
              value = numValue;
            }
          }

          // Handle nullable_date type: empty or "null" → null, otherwise keep as-is
          if (fieldConfig.type === 'nullable_date') {
            const strValue = (value as string || '').trim().toLowerCase();
            if (strValue === '' || strValue === 'null') {
              value = null;
            }
          }

          updateData[field] = value;
        }

        if (hasConversionError) {
          setProgress({ current: i + 1, total, successCount, errorCount });
          continue;
        }

        // Add updated_at timestamp
        updateData.updated_at = new Date().toISOString();

        // Execute update using helper function
        const { data: afterData, error: updateError } = await updateRecord(selectedTable, idField, recordId, updateData);

        if (updateError) {
          errorCount++;
          updateResults.push({
            rowIndex: i + 2,
            id: recordId,
            status: 'error',
            message: `Erro ao atualizar: ${updateError.message}`,
            before: beforeData,
            after: null,
          });
        } else {
          successCount++;
          updateResults.push({
            rowIndex: i + 2,
            id: recordId,
            status: 'success',
            message: 'Atualizado com sucesso',
            before: beforeData,
            after: afterData,
          });
        }
      } catch (error) {
        errorCount++;
        updateResults.push({
          rowIndex: i + 2,
          id: recordId,
          status: 'error',
          message: `Erro inesperado: ${error instanceof Error ? error.message : 'Desconhecido'}`,
          before: null,
          after: null,
        });
      }
      
      // Update progress after each record
      setProgress({ current: i + 1, total, successCount, errorCount });
    }

    setResults(updateResults);
    setShowResults(true);
    setIsProcessing(false);
    setProgress({ current: total, total, successCount, errorCount });
    
    toast({
      title: 'Processamento concluído',
      description: `${successCount} sucesso(s), ${errorCount} erro(s)`,
      variant: errorCount > 0 ? 'destructive' : 'default',
    });
  };

  // Export report
  const exportReport = () => {
    if (results.length === 0) return;

    const reportLines = [
      'Relatório de Atualização em Massa',
      `Data: ${new Date().toLocaleString('pt-BR')}`,
      `Tabela: ${tableConfig?.label || selectedTable}`,
      `Campos atualizados: ${selectedFields.join(', ')}`,
      '',
      `Total processado: ${results.length}`,
      `Sucessos: ${results.filter(r => r.status === 'success').length}`,
      `Erros: ${results.filter(r => r.status === 'error').length}`,
      `Ignorados: ${results.filter(r => r.status === 'skipped').length}`,
      '',
      '=== DETALHES ===',
      '',
    ];

    for (const result of results) {
      reportLines.push(`Linha ${result.rowIndex} | ID: ${result.id}`);
      reportLines.push(`  Status: ${result.status.toUpperCase()}`);
      reportLines.push(`  Mensagem: ${result.message}`);
      
      if (result.before && result.after) {
        reportLines.push('  Alterações:');
        for (const field of selectedFields) {
          const before = result.before[field];
          const after = result.after[field];
          if (before !== after) {
            reportLines.push(`    ${field}: "${before}" → "${after}"`);
          }
        }
      }
      reportLines.push('');
    }

    const blob = new Blob([reportLines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_atualizacao_${selectedTable}_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stats for results
  const resultStats = useMemo(() => ({
    total: results.length,
    success: results.filter(r => r.status === 'success').length,
    error: results.filter(r => r.status === 'error').length,
    skipped: results.filter(r => r.status === 'skipped').length,
  }), [results]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate('/configuracoes')}
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Atualização em Massa (CSV)</h1>
          <p className="text-sm text-muted-foreground">
            Atualize registros do banco de dados via upload de arquivo CSV
          </p>
        </div>
      </div>

      {/* Step 1: Select Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">1</span>
            Selecionar Tabela
          </CardTitle>
          <CardDescription>Escolha a tabela que deseja atualizar</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedTable} onValueChange={handleTableChange}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Selecione uma tabela..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SUPPORTED_TABLES).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Step 2: Select Fields */}
      {selectedTable && tableConfig && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">2</span>
              Campos Editáveis
            </CardTitle>
            <CardDescription>
              Campos disponíveis para atualização. O campo ID será usado como chave de correspondência.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Badge variant="outline" className="bg-muted">
                {idField} (chave)
              </Badge>
              {tableConfig.editableFields.map(field => (
                <div key={field.name} className="flex items-center gap-2">
                  <Checkbox
                    id={field.name}
                    checked={selectedFields.includes(field.name)}
                    onCheckedChange={() => toggleField(field.name)}
                  />
                  <Label htmlFor={field.name} className="text-sm cursor-pointer">
                    {field.label} <span className="text-muted-foreground">({field.name})</span>
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Upload File */}
      {selectedTable && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-sm">3</span>
              Importar Arquivo
            </CardTitle>
            <CardDescription>
              O arquivo deve ter headers idênticos aos nomes das colunas do banco.
              Primeira coluna obrigatória: <strong>{idField}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-sm whitespace-nowrap">Tipo de arquivo:</Label>
                <Select value={fileType} onValueChange={(value: FileType) => {
                  setFileType(value);
                  clearFile();
                }}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="xlsx">XLSX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Input
                  type="file"
                  accept={fileType === 'csv' ? '.csv' : '.xlsx,.xls'}
                  onChange={handleFileUpload}
                  className="cursor-pointer"
                  key={fileType} // Reset input when file type changes
                />
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="limit" className="text-sm whitespace-nowrap">Limite de linhas:</Label>
                <Input
                  id="limit"
                  type="number"
                  min="1"
                  placeholder="Todas"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  className="w-24"
                />
              </div>
            </div>

            {fileName && (
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                <span className="flex-1 text-sm">{fileName}</span>
                <Badge variant="secondary">{csvData.length} linhas</Badge>
                {limit && parseInt(limit) > 0 && parseInt(limit) < csvData.length && (
                  <Badge variant="outline">Limitado a {limit}</Badge>
                )}
                <Button variant="ghost" size="icon" onClick={clearFile}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Erros de Validação</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside mt-2">
                    {validationErrors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {csvData.length > 0 && validationErrors.length === 0 && !isProcessing && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPreview(true)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Pré-visualizar ({rowsToProcess.length} linhas)
                </Button>
                <Button onClick={() => setShowConfirmDialog(true)} disabled={isProcessing}>
                  <Upload className="h-4 w-4 mr-2" />
                  Executar Atualização
                </Button>
              </div>
            )}

            {/* Progress Section */}
            {isProcessing && (
              <div className="space-y-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="font-medium">Processando...</span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {progress.current} de {progress.total} registros
                  </span>
                </div>
                <Progress 
                  value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0} 
                  className="h-3"
                />
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-green-600 font-medium">{progress.successCount} sucesso(s)</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-red-600 font-medium">{progress.errorCount} erro(s)</span>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Não feche esta página até o processamento ser concluído.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {showResults && results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              Resultado do Processamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Badge variant="secondary" className="text-base px-3 py-1">
                Total: {resultStats.total}
              </Badge>
              <Badge className="bg-green-500 text-base px-3 py-1">
                Sucesso: {resultStats.success}
              </Badge>
              <Badge variant="destructive" className="text-base px-3 py-1">
                Erros: {resultStats.error}
              </Badge>
              <Badge variant="outline" className="text-base px-3 py-1">
                Ignorados: {resultStats.skipped}
              </Badge>
            </div>

            <ScrollArea className="h-[300px] border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Linha</TableHead>
                    <TableHead className="w-48">ID</TableHead>
                    <TableHead className="w-24">Status</TableHead>
                    <TableHead>Mensagem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((result, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{result.rowIndex}</TableCell>
                      <TableCell className="font-mono text-xs">{result.id}</TableCell>
                      <TableCell>
                        {result.status === 'success' && (
                          <Badge className="bg-green-500">Sucesso</Badge>
                        )}
                        {result.status === 'error' && (
                          <Badge variant="destructive">Erro</Badge>
                        )}
                        {result.status === 'skipped' && (
                          <Badge variant="outline">Ignorado</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{result.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <Button onClick={exportReport}>
              <Download className="h-4 w-4 mr-2" />
              Exportar Relatório Completo
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Pré-visualização do CSV</DialogTitle>
            <DialogDescription>
              Mostrando {rowsToProcess.length} de {csvData.length} linhas
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  {csvHeaders.map(header => (
                    <TableHead key={header}>{header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rowsToProcess.slice(0, 50).map((row, idx) => (
                  <TableRow key={idx}>
                    {csvHeaders.map(header => (
                      <TableCell key={header} className="text-sm">
                        {row[header] || '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rowsToProcess.length > 50 && (
              <p className="text-center text-sm text-muted-foreground py-4">
                Mostrando apenas as primeiras 50 linhas...
              </p>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirmar Atualização em Massa
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4 pt-4">
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Atenção! Esta ação é irreversível.</AlertTitle>
                  <AlertDescription>
                    Você está prestes a atualizar {rowsToProcess.length} registro(s) na tabela <strong>{tableConfig?.label}</strong>.
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-2 text-sm">
                  <p><strong>Campos que serão atualizados:</strong></p>
                  <div className="flex flex-wrap gap-1">
                    {selectedFields.map(field => (
                      <Badge key={field} variant="secondary">{field}</Badge>
                    ))}
                  </div>
                </div>

                {rowsToProcess.length > 100 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Volume Alto</AlertTitle>
                    <AlertDescription>
                      Você está atualizando mais de 100 registros. Considere testar com um limite menor primeiro.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={processUpdates} disabled={isProcessing}>
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Confirmar e Executar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkUpdate;
